import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Save, ExternalLink, Eye } from "lucide-react";
import { api, LANGS, formatApiErrorDetail } from "@/lib/api";
import { LANG_NAMES } from "@/lib/i18n";
import { StatusBadge } from "@/components/admin/StatusBadge";

const STATUSES = ["Missing", "Draft", "Review", "Published"];
const FIELDS = [
  ["h1", "H1"], ["seo_title", "SEO Title"], ["slug", "Slug"],
  ["meta_desc", "Meta Description"], ["intro", "Intro"], ["cta", "CTA"],
];

export default function EntityEditor() {
  const { entityId } = useParams();
  const qc = useQueryClient();
  const [lang, setLang] = useState("tr");
  const [form, setForm] = useState({});
  const [translating, setTranslating] = useState(false);
  const [saving, setSaving] = useState(false);

  const { data: entity, refetch } = useQuery({
    queryKey: ["cms-entity", entityId],
    queryFn: async () => (await api.get(`/cms/entities/${entityId}`)).data,
  });

  useEffect(() => {
    if (!entity) return;
    const tr = entity.translations?.[lang] || {};
    setForm({
      status: tr.status || "Missing",
      h1: tr.h1 || "", seo_title: tr.seo_title || "", slug: tr.slug || "",
      meta_desc: tr.meta_desc || "", intro: tr.intro || "", cta: tr.cta || "",
      sections: JSON.stringify(tr.sections || [], null, 2),
      faq: JSON.stringify(tr.faq || [], null, 2),
      cover_image: entity.meta?.image || "", date: entity.meta?.date || "",
    });
  }, [entity, lang]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    let sections, faq;
    try { sections = JSON.parse(form.sections || "[]"); faq = JSON.parse(form.faq || "[]"); }
    catch { setSaving(false); return toast.error("Sections/FAQ must be valid JSON"); }
    try {
      await api.put(`/cms/entities/${entityId}/translation/${lang}`, {
        status: form.status, h1: form.h1, seo_title: form.seo_title, slug: form.slug,
        meta_desc: form.meta_desc, intro: form.intro, cta: form.cta, sections, faq,
      });
      if (entity.type === "blog") {
        await api.put(`/cms/entities/${entityId}`, { meta: { image: form.cover_image, date: form.date } });
      }
      toast.success(`${LANG_NAMES[lang]} saved`);
      refetch(); qc.invalidateQueries({ queryKey: ["cms-entities"] });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const aiTranslate = async () => {
    setTranslating(true);
    try {
      await api.post("/cms/translate", { entity_id: entityId, target_lang: lang });
      toast.success(`AI draft generated for ${LANG_NAMES[lang]} (status: Draft)`);
      refetch();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setTranslating(false); }
  };

  if (!entity) return <p className="text-subtle">…</p>;
  const input = "w-full bg-obsidian border border-white/15 rounded-lg px-3 py-2 text-sm text-ink focus:border-copper transition-colors";

  return (
    <div>
      <Link to="/sbt-panel-7391/entities" className="inline-flex items-center gap-2 text-sm text-subtle hover:text-copper mb-4"><ArrowLeft size={16} /> Content</Link>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-tight text-ink">{entity.translations?.tr?.h1 || entityId}</h1>
          <p className="text-xs text-faint font-mono">{entityId} · {entity.type} · {entity.published ? "published" : "unpublished"}</p>
        </div>
        <div className="flex items-center gap-2">
          {(form.status === "Draft" || form.status === "Review") && (
            <a href={(form.slug ? `/${lang}/${form.slug}` : `/${lang}`) + "?preview=1"} target="_blank" rel="noopener noreferrer" data-testid="cms-preview-btn" className="btn-copper !py-2 !px-4 text-xs"><Eye size={14} /> Preview</a>
          )}
          {form.status === "Published" && (
            <a href={`/${lang}/${form.slug}`.replace(/\/$/, "") || `/${lang}`} target="_blank" rel="noopener noreferrer" className="btn-ghost !py-2 !px-4 text-xs"><ExternalLink size={14} /> View</a>
          )}
        </div>
      </div>

      {/* language tabs */}
      <div className="flex gap-2 mb-6 flex-wrap" data-testid="editor-lang-tabs">
        {LANGS.map((l) => (
          <button
            key={l} onClick={() => setLang(l)}
            data-testid={`editor-tab-${l}`}
            className={`px-4 py-2 rounded-lg text-sm border transition-colors ${lang === l ? "border-copper text-copper bg-copper/10" : "border-white/15 text-subtle hover:text-ink"}`}
          >
            {LANG_NAMES[l]} <StatusBadge status={entity.translations?.[l]?.status || "Missing"} />
          </button>
        ))}
      </div>

      <div className="card-surface !hover:translate-y-0 p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <label className="text-xs text-subtle">Status</label>
            <select data-testid="editor-status" value={form.status} onChange={set("status")} className={`${input} !w-auto`}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {lang !== "tr" && (
            <button data-testid="ai-translate-btn" onClick={aiTranslate} disabled={translating} className="btn-ghost !py-2 !px-4 text-xs disabled:opacity-60">
              <Sparkles size={14} /> {translating ? "Generating…" : "AI Draft from Turkish"}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, label]) => (
            <div key={k} className={k === "meta_desc" || k === "intro" ? "sm:col-span-2" : ""}>
              <label className="block text-xs text-subtle mb-1.5">{label}</label>
              {k === "meta_desc" || k === "intro" ? (
                <textarea data-testid={`field-${k}`} rows={2} value={form[k] || ""} onChange={set(k)} className={input} />
              ) : (
                <input data-testid={`field-${k}`} value={form[k] || ""} onChange={set(k)} className={input} />
              )}
            </div>
          ))}
        </div>

        <div>
          <label className="block text-xs text-subtle mb-1.5">Sections (JSON: [&#123;h2, body&#125;])</label>
          <textarea data-testid="field-sections" rows={5} value={form.sections || ""} onChange={set("sections")} className={`${input} font-mono text-xs`} />
        </div>
        <div>
          <label className="block text-xs text-subtle mb-1.5">FAQ (JSON: [&#123;q, a&#125;])</label>
          <textarea data-testid="field-faq" rows={5} value={form.faq || ""} onChange={set("faq")} className={`${input} font-mono text-xs`} />
        </div>

        {entity.type === "blog" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-white/10">
            <div>
              <label className="block text-xs text-subtle mb-1.5">Cover Image URL</label>
              <input data-testid="field-cover-image" value={form.cover_image || ""} onChange={set("cover_image")} className={input} />
            </div>
            <div>
              <label className="block text-xs text-subtle mb-1.5">Published Date (YYYY-MM-DD)</label>
              <input data-testid="field-date" value={form.date || ""} onChange={set("date")} className={input} />
            </div>
          </div>
        )}

        <button data-testid="editor-save" onClick={save} disabled={saving} className="btn-copper disabled:opacity-60"><Save size={16} /> {saving ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}
